import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  Eye, 
  User, 
  Clock, 
  MapPin, 
  Calendar,
  RefreshCw,
  ChevronDown,
  ChevronRight,
  Camera,
  FileText,
  Binoculars,
  AlertTriangle,
  CheckCircle
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { format } from 'date-fns';

interface PatrolObservation {
  id: string;
  title: string;
  description: string;
  severity: 'low' | 'medium' | 'high';
  status: 'pending' | 'resolved';
  timestamp: string;
  guard_name: string;
  guard_id: string;
  image_url?: string;
  team_id: string;
  location?: string;
  notes?: any[];
}

interface PatrolObservationsListProps {
  teamId: string;
  dateFilter?: { start: Date; end: Date };
}

const getSeverityColor = (severity: string) => {
  switch (severity) {
    case 'high': return 'bg-red-100 text-red-800 border-red-200';
    case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    case 'low': return 'bg-green-100 text-green-800 border-green-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

const getStatusColor = (status: string) => {
  switch (status) {
    case 'resolved': return 'bg-green-100 text-green-800 border-green-200';
    case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    default: return 'bg-gray-100 text-gray-800 border-gray-200';
  }
};

export const PatrolObservationsList: React.FC<PatrolObservationsListProps> = ({ 
  teamId, 
  dateFilter 
}) => {
  const [refreshing, setRefreshing] = useState(false);
  const [expandedObservations, setExpandedObservations] = useState<Set<string>>(new Set());

  // Get guard names for all observations
  const { data: guardNamesMap } = useQuery({
    queryKey: ['guardNamesForPatrolObservations', teamId],
    queryFn: async () => {
      const { data } = await supabase
        .from('profiles')
        .select('id, full_name, first_name, last_name')
        .eq('role', 'guard');
      
      const nameMap: Record<string, string> = {};
      data?.forEach((profile) => {
        nameMap[profile.id] = profile.full_name || 
          `${profile.first_name || ''} ${profile.last_name || ''}`.trim() || 
          'Unknown Guard';
      });
      
      return nameMap;
    }
  });

  const { data: observations, isLoading, refetch } = useQuery({
    queryKey: ['patrol-observations-list', teamId, dateFilter],
    queryFn: async () => {
      let query = supabase
        .from('patrol_observations')
        .select('*')
        .eq('team_id', teamId)
        .order('timestamp', { ascending: false });

      if (dateFilter) {
        query = query
          .gte('timestamp', dateFilter.start.toISOString())
          .lte('timestamp', dateFilter.end.toISOString());
      }

      const { data, error } = await query;
      if (error) throw error;
      return data as PatrolObservation[];
    }
  });

  const handleRefresh = async () => {
    setRefreshing(true);
    await refetch();
    setRefreshing(false);
  };

  const toggleObservationExpansion = (observationId: string) => {
    const newExpanded = new Set(expandedObservations);
    if (newExpanded.has(observationId)) {
      newExpanded.delete(observationId);
    } else {
      newExpanded.add(observationId);
    }
    setExpandedObservations(newExpanded);
  };

  // Auto-refresh every 30 seconds
  React.useEffect(() => {
    const interval = setInterval(() => {
      refetch();
    }, 30000);
    return () => clearInterval(interval);
  }, [refetch]);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6 text-center">
          <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4" />
          <p className="text-muted-foreground">Loading patrol observations...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Eye className="h-5 w-5 text-blue-500" />
          <h3 className="text-lg font-semibold">
            Patrol Observations ({observations?.length || 0})
          </h3>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleRefresh}
          disabled={refreshing}
          className="flex items-center gap-2"
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Observations List */}
      {!observations || observations.length === 0 ? (
        <Card>
          <CardContent className="p-6 text-center">
            <Eye className="w-12 h-12 text-muted-foreground mx-auto mb-4 opacity-50" />
            <h3 className="text-lg font-medium mb-2">No Patrol Observations Found</h3>
            <p className="text-muted-foreground">
              No patrol observations found for this team.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {observations.map((observation) => {
            const guardName = observation.guard_id ? guardNamesMap?.[observation.guard_id] : null;
            const isExpanded = expandedObservations.has(observation.id);
            
            return (
              <Card key={observation.id} className="overflow-hidden">
                <Collapsible open={isExpanded} onOpenChange={() => toggleObservationExpansion(observation.id)}>
                  <CollapsibleTrigger asChild>
                    <div className="w-full cursor-pointer hover:bg-muted/50 transition-colors">
                      <CardHeader className="pb-3">
                        <div className="flex items-start justify-between">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="flex-shrink-0">
                              {isExpanded ? (
                                <ChevronDown className="h-5 w-5 text-muted-foreground" />
                              ) : (
                                <ChevronRight className="h-5 w-5 text-muted-foreground" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <CardTitle className="text-base font-medium truncate">
                                {observation.title}
                              </CardTitle>
                              <div className="flex items-center gap-4 mt-1 text-sm text-muted-foreground">
                                <span>{format(new Date(observation.timestamp), 'dd/MM/yyyy HH:mm')}</span>
                                {guardName && <span>Guard: {guardName}</span>}
                                {observation.location && <span>Location: {observation.location}</span>}
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                            <Badge className={`${getSeverityColor(observation.severity)} text-xs`}>
                              {observation.severity}
                            </Badge>
                            <Badge className={`${getStatusColor(observation.status)} text-xs`}>
                              {observation.status}
                            </Badge>
                          </div>
                        </div>
                      </CardHeader>
                    </div>
                  </CollapsibleTrigger>
                  
                  <CollapsibleContent>
                    <CardContent className="pt-0">
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                        {/* Guard */}
                        {guardName && (
                          <div className="flex items-center gap-2">
                            <User className="h-4 w-4 text-blue-500" />
                            <span className="text-muted-foreground">Observer:</span>
                            <span className="font-medium truncate">{guardName}</span>
                          </div>
                        )}
                        
                        {/* Location */}
                        {observation.location && (
                          <div className="flex items-center gap-2">
                            <MapPin className="h-4 w-4 text-muted-foreground" />
                            <span className="text-muted-foreground">Location:</span>
                            <span className="font-medium truncate">{observation.location}</span>
                          </div>
                        )}
                        
                        {/* Date */}
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground" />
                          <span className="text-muted-foreground">Observed:</span>
                          <span className="font-medium">
                            {format(new Date(observation.timestamp), 'dd/MM/yyyy HH:mm')}
                          </span>
                        </div>

                        {/* Status */}
                        <div className="flex items-center gap-2">
                          {observation.status === 'resolved' ? (
                            <CheckCircle className="h-4 w-4 text-green-500" />
                          ) : (
                            <Clock className="h-4 w-4 text-yellow-500" />
                          )}
                          <span className="text-muted-foreground">Status:</span>
                          <span className="font-medium capitalize">{observation.status}</span>
                        </div>

                        {/* Severity */}
                        <div className="flex items-center gap-2">
                          {observation.severity === 'high' ? (
                            <AlertTriangle className="h-4 w-4 text-red-500" />
                          ) : observation.severity === 'medium' ? (
                            <Eye className="h-4 w-4 text-yellow-500" />
                          ) : (
                            <Binoculars className="h-4 w-4 text-green-500" />
                          )}
                          <span className="text-muted-foreground">Severity:</span>
                          <span className="font-medium capitalize">{observation.severity}</span>
                        </div>
                      </div>

                      {/* Description */}
                      {observation.description && (
                        <div className="mt-4 pt-4 border-t">
                          <h4 className="font-medium mb-2 flex items-center gap-2">
                            <FileText className="h-4 w-4 text-gray-600" />
                            Observation Details
                          </h4>
                          <p className="text-sm text-muted-foreground">{observation.description}</p>
                        </div>
                      )}

                      {/* Photo Display */}
                      {observation.image_url && (
                        <div className="mt-4 pt-4 border-t">
                          <h4 className="font-medium mb-3 flex items-center gap-2">
                            <Camera className="h-4 w-4 text-blue-600" />
                            Photo Evidence
                          </h4>
                          <div className="relative">
                            <img 
                              src={observation.image_url} 
                              alt="Patrol observation evidence" 
                              className="w-full max-w-md rounded-lg border shadow-sm"
                              onError={(e) => {
                                const target = e.target as HTMLImageElement;
                                target.style.display = 'none';
                              }}
                            />
                          </div>
                        </div>
                      )}

                      {/* Notes */}
                      {observation.notes && observation.notes.length > 0 && (
                        <div className="mt-4 pt-4 border-t">
                          <h4 className="font-medium mb-3">Notes ({observation.notes.length})</h4>
                          <div className="space-y-2">
                            {observation.notes.map((note: any, index: number) => (
                              <div key={index} className="p-3 bg-muted rounded-lg">
                                <p className="text-sm">{note.content || note}</p>
                                {note.created_at && (
                                  <p className="text-xs text-muted-foreground mt-1">
                                    {format(new Date(note.created_at), 'dd/MM/yyyy HH:mm')}
                                  </p>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </CollapsibleContent>
                </Collapsible>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};
